import type { CcpSections } from "../../types"

import { COMPANY_NAME } from "../../utils/branding"

/**
 * Customer Centric Plan — section catalogue.
 *
 * Titles are the exact Word/handover strings (CCP template). Do not translate
 * or reword them; where a heading names the tenant's own company it
 * interpolates COMPANY_NAME (`organization_name`, #2768) so every tenant sees
 * its own name. All ten sections are AI-generated (Update + Expand on every
 * tile).
 */

export type CcpSectionKey = keyof CcpSections

export type CcpSectionKind = "prose" | "bullets" | "buying" | "potential" | "engage"

export type CcpSectionDef = {
  key: CcpSectionKey
  /** Two-digit section number as shown in the Word template. */
  no: string
  /** Exact Word/handover title. */
  title: string
  kind: CcpSectionKind
  /** Field key sent to the per-field Update/PATCH endpoints (section-scoped). */
  fieldKey: CcpSectionKey
  /** Canvas tile placement on the 12-column fixed-tile grid. */
  canvas: { colSpan: number; rowSpan: number }
}

export const CCP_SECTIONS: CcpSectionDef[] = [
  {
    key: "overview",
    no: "01",
    title: "Customer overview",
    kind: "prose",
    fieldKey: "overview",
    canvas: { colSpan: 8, rowSpan: 1 },
  },
  {
    key: "buying",
    no: "02",
    title: "Customer Relationship & Buying Behavior",
    kind: "buying",
    fieldKey: "buying",
    canvas: { colSpan: 4, rowSpan: 2 },
  },
  {
    key: "potential",
    no: "03",
    title: "Customer Potential",
    kind: "potential",
    fieldKey: "potential",
    canvas: { colSpan: 4, rowSpan: 1 },
  },
  {
    key: "market",
    no: "04",
    title: "Customers Market overview",
    kind: "prose",
    fieldKey: "market",
    canvas: { colSpan: 4, rowSpan: 1 },
  },
  {
    key: "players",
    no: "05",
    title: "Key Players in the Market",
    kind: "prose",
    fieldKey: "players",
    canvas: { colSpan: 4, rowSpan: 1 },
  },
  {
    key: "model",
    no: "06",
    title: "Customer Business Model",
    kind: "prose",
    fieldKey: "model",
    canvas: { colSpan: 4, rowSpan: 1 },
  },
  {
    key: "engage",
    no: "07",
    title: `Current business engagement with ${COMPANY_NAME}`,
    kind: "engage",
    fieldKey: "engage",
    canvas: { colSpan: 8, rowSpan: 1 },
  },
  {
    key: "needs",
    no: "08",
    title: "TOP customer needs & pain points",
    kind: "bullets",
    fieldKey: "needs",
    canvas: { colSpan: 4, rowSpan: 1 },
  },
  {
    key: "value",
    no: "09",
    title: `Value Proposition of ${COMPANY_NAME}`,
    kind: "bullets",
    fieldKey: "value",
    canvas: { colSpan: 4, rowSpan: 1 },
  },
  {
    key: "activities",
    no: "10",
    title: "Key Activities",
    kind: "bullets",
    fieldKey: "activities",
    canvas: { colSpan: 4, rowSpan: 1 },
  },
]

/** Fixed spend-line labels for §03 Customer Potential (domain terms, not translated). */
export const CCP_POTENTIAL_LINES: Array<{ key: keyof CcpSections["potential"]; label: string }> = [
  { key: "overall", label: "Overall potential" },
  { key: "plastics", label: "Plastics" },
  { key: "boxBuild", label: "Box build" },
  { key: "upSell", label: "Up-sell" },
  { key: "crossSell", label: "Cross-sell" },
]
